import React from 'react';
import { Group, Text } from '@mantine/core';
import { IconArrowDownRight, IconArrowUpRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export type BlockVerdict = 'up' | 'down' | 'same' | 'new' | 'incomparable-grade' | 'incomparable';

export interface BlockComparisonRow {
  block: string;
  /** Ключ блока с бэкенда: по нему находится строка итоговой оценки. */
  questionId?: string;
  current: number;
  responseCount: number;
  previous?: number;
  previousResponseCount?: number;
  delta?: number;
  verdict: BlockVerdict;
}

/** Динамика раунда в том виде, в каком её отдаёт /results. */
export interface RoundComparisonView {
  previousRoundTitle: string;
  professionChanged: boolean;
  blocks: BlockComparisonRow[];
}

/** Меньше трёх ответов — среднее слишком шумное, чтобы читать движение как сигнал. */
const FEW_RESPONSES = 3;

/**
 * Движение блока с прошлого раунда.
 *
 * Намеренно набрано типографикой, а не плашкой: рядом уже стоят кольцо с
 * оценкой и подпись ожиданий, и третий цветной элемент спорил бы с ними за
 * внимание. Цвет живёт только на стрелке — сама оценка окрашена шкалой
 * ожиданий, и красить тем же зелёным «вырос» рядом с оранжевым «ниже
 * ожиданий» значило бы сказать о строке две разные вещи одновременно.
 *
 * Число ответов стоит рядом с дельтой, а не прячется: разницу дают две разные
 * группы ревьюеров, и часть её — про состав панели, а не про человека. Когда
 * ответов совсем мало, аннотация гаснет целиком; лишних слов при этом не
 * добавляем — счётчик и так всё говорит.
 */
export const BlockDelta: React.FC<{ row?: BlockComparisonRow }> = ({ row }) => {
  const { t } = useTranslation();
  if (!row) return null;

  const counts =
    row.previousResponseCount !== undefined
      ? // count управляет склонением («по 1 ответу» против «по 4 ответам»);
        // само число в строку подставляет current.
        t('perfReview.dynamics.responses', {
          count: row.responseCount,
          previous: row.previousResponseCount,
          current: row.responseCount,
        })
      : null;

  if (row.verdict === 'new') {
    return (
      <Text size="xs" c="dimmed">
        {t('perfReview.dynamics.new')}
      </Text>
    );
  }

  const was = t('perfReview.dynamics.was', { score: row.previous?.toFixed(1) });

  if (row.verdict === 'incomparable-grade' || row.verdict === 'incomparable') {
    return (
      <Text size="xs" c="dimmed">
        {was} ·{' '}
        {row.verdict === 'incomparable-grade'
          ? t('perfReview.dynamics.gradeChanged')
          : t('perfReview.dynamics.gradeUnknown')}
      </Text>
    );
  }

  const isNoisy =
    row.responseCount < FEW_RESPONSES || (row.previousResponseCount ?? 0) < FEW_RESPONSES;

  if (row.verdict === 'same') {
    return (
      <Text size="xs" c="dimmed">
        {t('perfReview.dynamics.same')}
        {counts ? ` · ${counts}` : ''}
      </Text>
    );
  }

  const isUp = row.verdict === 'up';
  const Arrow = isUp ? IconArrowUpRight : IconArrowDownRight;
  const arrowColor = isNoisy
    ? 'var(--mantine-color-dimmed)'
    : `var(--mantine-color-${isUp ? 'teal' : 'orange'}-7)`;

  return (
    // Читалке нужен контекст: без него «+0.8» звучит как число ниоткуда.
    <Group
      gap={4}
      wrap="nowrap"
      align="center"
      role="group"
      aria-label={t(`perfReview.dynamics.a11y.${row.verdict}`, {
        delta: Math.abs(row.delta ?? 0).toFixed(1),
      })}
    >
      <Arrow size={14} style={{ color: arrowColor, flexShrink: 0 }} aria-hidden />
      <Text
        size="xs"
        fw={600}
        c={isNoisy ? 'dimmed' : undefined}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {isUp ? '+' : '−'}
        {Math.abs(row.delta ?? 0).toFixed(1)}
      </Text>
      {counts && (
        <Text size="xs" c="dimmed">
          · {counts}
        </Text>
      )}
    </Group>
  );
};
